/* GASSISA
========================================================================================
* Descripción: Modal para reimpresión de tickets de remisiones en Venta Super
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 03/05/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Mayor visibilidad en datos clave de reimpresión
*      3       | 26/06/2026 | Lic. Jose Luis Aragon Cervantes  | Distribucion responsiva para modal portatil
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import { Fragment, useState, useEffect } from "react";
import { Box, Typography, Button, IconButton, CircularProgress, Select, MenuItem, FormControl, InputLabel, TableContainer, Paper } from "@mui/material";
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';

import SizeSoftXTableVentas from "../../leyout/SizeSoftXTableVentas";

import { useSesion } from "../../../context/System/SesionContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useRequest } from "../../../context/System/RequestContext";
import { useVentas } from "../../../context/Ventas/VentasContext";
import { apiObtenerRemisionVenta, apiListadoVentasCaja } from "../../../settings/ApiConfig";
import { leerMensajeErrorPrinterTools } from "../../../settings/printerTools";
import SizeSoftXModalPortatil from "../leyout/SizeSoftXModalPortatil";

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
    }).format(value || 0);
};

const formatDateTable = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleString('es-MX', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const encodeTicketDatoClave = (textEncoder, texto) => (
    textEncoder.encode('\x1B\x45\x01\x1B\x21\x10' + texto + '\x1B\x21\x00\x1B\x45\x00')
);

export default function ModalReimpresionRemisiones(props) {
    const { openModal = false, onClose, caja } = props;
    
    const { handleOpenAlert } = useLayout();
    const { GetRequest } = useRequest();
    const { parametrosCajaForm } = useSesion();
    const { 
        listaCajas, 
        handleOntenerListaCajasRegistro 
    } = useVentas();

    const [cajaSeleccionada, setCajaSeleccionada] = useState("");
    const [fechaInicio, setFechaInicio] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
    const [fechaFin, setFechaFin] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [loadingImpresion, setLoadingImpresion] = useState(false);
    const [remisionesAgrupadas, setRemisionesAgrupadas] = useState([]);
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);

    const cajaActual = cajaSeleccionada;

    useEffect(() => {
        if (openModal) {
            handleOntenerListaCajasRegistro();
        }
    }, [openModal]);

    useEffect(() => {
        if (parametrosCajaForm?.caja && parametrosCajaForm.caja !== 'CAJA-DEFAULT') {
            setCajaSeleccionada(parametrosCajaForm.caja);
        } else if (listaCajas && listaCajas.length > 0 && !cajaSeleccionada) {
            setCajaSeleccionada(listaCajas[0]);
        }
    }, [parametrosCajaForm, listaCajas]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (openModal && cajaSeleccionada && listaCajas.length > 0) {
                handleBuscarDatos();
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [openModal, cajaSeleccionada, fechaInicio, fechaFin]);

    const handleCajaChange = (event) => {
        setCajaSeleccionada(event.target.value);
        setVentaSeleccionada(null);
    };

    const handleFechaInicioChange = (event) => {
        const dateStr = event.target.value;
        setFechaInicio(new Date(dateStr + 'T12:00:00'));
    };

    const handleFechaFinChange = (event) => {
        const dateStr = event.target.value;
        setFechaFin(new Date(dateStr + 'T12:00:00'));
    };

    const getFechaValue = (fecha) => {
        if (!fecha) return "";
        const d = new Date(fecha);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateTimeQuery = (date) => {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    };

    const handleBuscarDatos = async () => {
        const caja = (cajaSeleccionada || parametrosCajaForm?.caja || "").trim();
        if (!caja || caja === 'CAJA-DEFAULT') {
            handleOpenAlert("Seleccione una caja para buscar", "warning");
            return;
        }

        setLoading(true);
        setVentaSeleccionada(null);

        try {
            const params = new URLSearchParams();
            params.append('fechainicial', formatDateTimeQuery(fechaInicio));
            params.append('fechafinal', formatDateTimeQuery(fechaFin));
            const url = `${apiListadoVentasCaja.replace('{pc}', caja)}?${params.toString()}`;
            
            const response = await GetRequest({ url });
            
            if (!response.hasError && response.result) {
                const ventasOrdenadas = (response.result || []).sort((a, b) => {
                    return String(a.remision ?? "").localeCompare(String(b.remision ?? ""));
                });
                const agrupadas = agruparVentasPorRemision(ventasOrdenadas);
                setRemisionesAgrupadas(agrupadas);
            } else {
                setRemisionesAgrupadas([]);
            }
        } catch (error) {
            handleOpenAlert("Error al buscar datos: " + error.message, "error");
            setRemisionesAgrupadas([]);
        } finally {
            setLoading(false);
        }
    };

    const agruparVentasPorRemision = (ventas) => {
        const mapa = new Map();
        
        ventas.forEach((v, index) => {
            const remision = v.remision;
            if (!remision) return;
            
            if (!mapa.has(remision)) {
                mapa.set(remision, {
                    id: index,
                    remision: remision,
                    fechaventa: v.fechaventa,
                    pc: v.pc,
                    razonsocial: v.razonsocial || "PUBLICO GENERAL",
                    totalarticulos: 0,
                    total: 0
                });
            }
            
            const entry = mapa.get(remision);
            entry.totalarticulos += Number(v.cantidadarticulo || 0);
            entry.total += Number(v.totalarticulo || 0);
        });
        
        return Array.from(mapa.values()).sort((a, b) => {
            return String(a.remision ?? "").localeCompare(String(b.remision ?? ""));
        });
    };

    const handleSelectVenta = (venta) => {
        setVentaSeleccionada(venta);
    };

    const handleDoubleClickVenta = (venta) => {
        setVentaSeleccionada(venta);
        setTimeout(() => imprimirTicketRemision(venta), 100);
    };

    const imprimirTicketRemision = async (ventaEspecifica = null) => {
        const remision = ventaEspecifica?.remision || ventaSeleccionada?.remision || remisionesAgrupadas[0]?.remision;
        
        if (!remision) {
            handleOpenAlert("No hay remisiones disponibles para imprimir", "warning");
            return;
        }

        setLoadingImpresion(true);

        try {
            const url = apiObtenerRemisionVenta
                .replace("{remision}", remision)
                .replace("{caja}", (cajaSeleccionada || parametrosCajaForm?.caja || "").trim());
            
            const response = await GetRequest({ url });
            
            if (response.hasError || !response.result) {
                handleOpenAlert("No se pudo obtener la remisión", "error");
                setLoadingImpresion(false);
                return;
            }

            const dataTicket = response.result;
            const formData = new FormData();
            const textEncoder = new TextEncoder();

            let ticketParts = [
                textEncoder.encode('\x1B\x40'),
                textEncoder.encode('\x1B\x4B\x60'), // Retroceso de papel para encimar logo
            ];

            // Encabezado
            ticketParts.push(
                textEncoder.encode('\x1B\x33\x00'), // Espaciado mínimo
                textEncoder.encode('\x1B\x61\x01\x1B\x21\x00' + (dataTicket.encabezado || "").trim() + '\n'),
                textEncoder.encode('\x1B\x32'), // Restaurar espaciado
                textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
                textEncoder.encode('Caja: ' + (dataTicket.cabecero.pc || "") + '\n'),
                encodeTicketDatoClave(textEncoder, 'Remision: ' + (dataTicket.cabecero.remision || "") + '\n'),
                textEncoder.encode('Vendedor: ' + (dataTicket.cabecero.usuario || "") + '\n'),
                encodeTicketDatoClave(textEncoder, 'Fecha: ' + (dataTicket.cabecero.fechaventa || "") + '\n'),
                textEncoder.encode('Tipo de Pago: ' + (dataTicket.cabecero.tipopago || "") + '\n'),
                encodeTicketDatoClave(textEncoder, 'Cliente: ' + (dataTicket.cabecero.razonsocial || "No especificado") + '\n'),
                textEncoder.encode('-'.repeat(32) + '\n')
            );

            // Detalles de artículos
            dataTicket.detalles.forEach((articulo) => {
                ticketParts.push(textEncoder.encode('\x1B\x45\x01' + (articulo.descripcion || "Sin descripcion") + '\x1B\x45\x00' + '\n'));
                ticketParts.push(textEncoder.encode('Cantidad: ' + (articulo.cantidad || 0) + ' - Talla: ' + (articulo.medida || 0) + '\n'));
                ticketParts.push(textEncoder.encode('P.Unit: $' + (articulo.costounitario || 0).toFixed(2) + '  Desc: $' + (articulo.descuento || 0).toFixed(2) + '\n'));
                ticketParts.push(textEncoder.encode('Importe: $' + (articulo.total || 0).toFixed(2) + '\n'));
                ticketParts.push(textEncoder.encode('Vendedor: ' + (articulo.vendedorpreventa?.trim() || "N/A") + '\n'));
                ticketParts.push(textEncoder.encode('Origen: ' + (articulo.almacenexhibidor?.trim() || "N/A") + '\n'));
                ticketParts.push(textEncoder.encode('.'.repeat(32) + '\n'));
            });

            // Totales
            const cabValeReimpP = dataTicket.cabecero || {};
            const folioValeReimpP = Number(cabValeReimpP.foliovaleporcambio ?? cabValeReimpP.FolioValePorCambio ?? cabValeReimpP.foliovale ?? 0) || 0;
            const montoValeReimpP = Number(cabValeReimpP.montovaleporcambio ?? cabValeReimpP.MontoValePorCambio ?? cabValeReimpP.montovale ?? 0) || 0;
            const hayValeReimpP = folioValeReimpP > 0 && montoValeReimpP > 0;

            ticketParts.push(
                textEncoder.encode('-'.repeat(32) + '\n'),
                textEncoder.encode('\x1B\x61\x00Subtotal: '),
                textEncoder.encode('\x1B\x61\x02$' + (dataTicket.cabecero.subtotal || 0).toFixed(2) + '\n'),
                textEncoder.encode('\x1B\x61\x00IVA: '),
                textEncoder.encode('\x1B\x61\x02$' + (dataTicket.cabecero.iva || 0).toFixed(2) + '\n')
            );

            if (hayValeReimpP) {
                ticketParts.push(
                    textEncoder.encode('\x1B\x61\x00Folio Vale: '),
                    textEncoder.encode('\x1B\x61\x02' + folioValeReimpP + '\n'),
                    textEncoder.encode('\x1B\x61\x00Descto Vale: '),
                    textEncoder.encode('\x1B\x61\x02$' + montoValeReimpP.toFixed(2) + '\n')
                );
            }

            ticketParts.push(
                textEncoder.encode('\x1B\x61\x00Total: '),
                textEncoder.encode('\x1B\x61\x02$' + (dataTicket.cabecero.total || 0).toFixed(2) + '\n')
            );

            // Tipo de pago (si no es efectivo)
            if ((dataTicket.cabecero.tipopago || "").trim() !== "EFECTIVO") {
                ticketParts.push(textEncoder.encode('\x1B\x61\x00Total tipo pago: '));
                ticketParts.push(textEncoder.encode('\x1B\x61\x02$' + (dataTicket.cabecero.totaltipopago || 0).toFixed(2) + '\n'));
            }

            // Efectivo y Cambio
            ticketParts.push(
                textEncoder.encode('\x1B\x61\x00Efectivo: '),
                textEncoder.encode('\x1B\x61\x02$' + (dataTicket.cabecero.totalefectivo || 0).toFixed(2) + '\n'),
                textEncoder.encode('\x1B\x61\x00Cambio: '),
                textEncoder.encode('\x1B\x61\x02$' + (dataTicket.cabecero.totalcambio || 0).toFixed(2) + '\n'),
                textEncoder.encode('-'.repeat(32) + '\n'),
                // Pie de ticket - Política de cambios
                textEncoder.encode('\x1B\x61\x00' + '-'.repeat(32) + '\n')
            );

            // Pie configurado
            const pieConfigurado = (parametrosCajaForm.pieticket || "");
            if (pieConfigurado.length > 0) {
                ticketParts.push(textEncoder.encode('\x1B\x61\x01' + pieConfigurado + '\n'));
            }

            // Pie final + reimpresión
            ticketParts.push(
                textEncoder.encode('\x1B\x61\x01' + 'REIMPRESION VENTA\n'),
                textEncoder.encode('\x1B\x61\x01' + 'Vuelva pronto\n\n\n\n\n'),
                textEncoder.encode('\x1D\x56\x00')
            );

            const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
            const finalTicket = new Uint8Array(totalLength);
            let offset = 0;
            ticketParts.forEach(part => {
                finalTicket.set(part, offset);
                offset += part.length;
            });

            formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
            formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

            const logoBase64 = parametrosCajaForm?.logo;
            if (logoBase64) {
                try {
                    const binary = atob(logoBase64);
                    const bytes = new Uint8Array(binary.length);
                    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                    formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
                } catch (err) {
                    // Logo no disponible, continuar sin él
                }
            }

            const respImpresion = await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });

            if (!respImpresion.ok) {
                handleOpenAlert("Error al reimprimir ticket: " + (await leerMensajeErrorPrinterTools(respImpresion, respImpresion.statusText)), "error");
                return;
            }

            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        } finally {
            setLoadingImpresion(false);
        }
    };

    return (
        <SizeSoftXModalPortatil
            open={openModal}
            onClose={onClose}
            className="modal-reimpresion-remisiones-portatil"
            sx={{ width: 'min(94vw, 820px)', p: { xs: 1.25, sm: 2 } }}
        >
            <Box className="reimpresion-remisiones-portatil__header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    Reimpresión de Tickets de ventas
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box className="reimpresion-remisiones-portatil__filters" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Caja</InputLabel>
                    <Select
                        value={cajaSeleccionada}
                        label="Caja"
                        onChange={handleCajaChange}
                    >
                        {(listaCajas || []).map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <CalendarTodayIcon fontSize="small" />
                <TextField autoComplete="off"
                    type="date"
                    label="Fecha inicial"
                    value={getFechaValue(fechaInicio)}
                    onChange={handleFechaInicioChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }}
                />
                <Typography>-</Typography>
                <TextField autoComplete="off"
                    type="date"
                    label="Fecha final"
                    value={getFechaValue(fechaFin)}
                    onChange={handleFechaFinChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }}
                />
                <Button 
                    variant="contained" 
                    startIcon={<SearchIcon />}
                    onClick={handleBuscarDatos}
                >
                    Buscar
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer className="reimpresion-remisiones-portatil__table" component={Paper} sx={{ maxHeight: '40vh', overflow: 'auto', '& .MuiTable-root': { borderCollapse: 'collapse' }, '& .MuiTableCell-root': { padding: '2px 4px' } }}>
                    <SizeSoftXTableVentas
                        columns={[
                            { title: "Fecha", field: "fechaventa", render: (row) => formatDateTable(row.fechaventa), width: 180 },
                            { title: "Remisión", field: "remision", width: 90 },
                            { title: "Monto", field: "total", render: (row) => formatCurrency(row.total), align: "center", width: 80 },
                            { title: "Caja", field: "pc", align: "center", width: 80 },
                            { title: "Arts", field: "totalarticulos", align: "center", width: 50 },
                            { title: "Cliente", field: "razonsocial" },
                        ]}
                        data={remisionesAgrupadas}
                        id="id"
                        row={ventaSeleccionada}
                        setRow={handleSelectVenta}
                        fnDoubleClick={handleDoubleClickVenta}
                        fontSize="small"
                        rowsPerPageIntro={100}
                        disablePagination={true}
                        disableStickyHeader={false}
                    />
                </TableContainer>
            )}

            <Box className="reimpresion-remisiones-portatil__footer" sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<PrintIcon />}
                    onClick={imprimirTicketRemision}
                >
                    Imprimir Ticket
                </Button>
                <Button variant="outlined" onClick={onClose}>
                    Cerrar
                </Button>
            </Box>
        </SizeSoftXModalPortatil>
    );
}
